'use strict';

import {convertDataVToVue} from '../apply_vue_data_directives.js';
import {load_google_charts, load_corecharts} from './google_charts.js';

const REFRESH_INTERVAL_MS = 60000;
const CHART_LOAD_TIMEOUT_MS = 10000;
const PAGER_WINDOW = 10;
const MINIMUM_SCREEN_WIDTH_PX = 768;

export class Campaigns {
	constructor(component_id) {
		this.id = component_id;
		this.vars = yalwa.v.components[this.id];
		this.$root = jQuery(`.js-${this.id}`);
	}

	init() {
		// everything is handled by the Vue component
	}

	vue_setup(Vue) {
		const ythis = this;

		const root_el = document.querySelector(`.js-${this.id}`);
		if (!root_el) {
			console.error(`Vue JS container not found for ID: ${this.id}`);
			return {};
		}

		const sort_labels = Object.fromEntries(Array.from(root_el.querySelectorAll('.campaigns__sort_menu > yalwa-dropdown-item')).map(item => [item.getAttribute('value'), item.getAttribute('label')]));
		const template = `${convertDataVToVue(root_el)}`;
		const initial = ythis.vars?.data ?? {};
		const l = ythis.vars?.labels ?? {};
		const csrf = ythis.vars?.csrf ?? {};

		return Vue.defineComponent({
			delimiters: ['[[', ']]'],
			name: this.id,
			template,
			setup() {
				const s = Vue.reactive({
					campaigns: [],
					total: 0,
					page: 1,
					pages: 1,
					shown: 0,
					kpis: {active: 0, impressions: 0, clicks: 0},
					wallet: {formatted: '', low: false, url: ''},
					overview: null,
					filter: 'all',
					sort: 'created',
					descending: true,
					dashboard_url: '',
					creation_url: ''
				});
				Object.keys(initial).forEach(key => {
					s[key] = initial[key];
				});

				const busy = Vue.ref(false);
				const action_busy = Vue.ref(false);
				const error_message = Vue.ref('');
				const chart_failed = Vue.ref(false);
				const chart_metric = Vue.ref('clicks');
				const chart_metrics = [
					{value: 'clicks', label: l.series_clicks},
					{value: 'impressions', label: l.series_impressions},
					{value: 'spent', label: l.series_spent}
				];
				const expanded = Vue.reactive({});
				const performance_expanded = Vue.reactive({});
				const supported_screen_media = window.matchMedia(`(min-width: ${MINIMUM_SCREEN_WIDTH_PX}px)`);
				const desktop_available = Vue.ref(supported_screen_media.matches);

				let end_dialog = null;
				let desktop_dialog = null;
				let remove_dialog_listeners = () => {};
				let in_flight = false;
				let disposed = false;
				let refresh_timer = null;
				let chart = null;
				let chart_data = null;
				let chart_version = 0;
				let resize_timer = null;
				let number_format = null;
				let compact_format = null;

				const filters = [
					{value: 'all', label: l.filter_all || ''},
					{value: 'active', label: l.filter_active || ''},
					{value: 'paused', label: l.filter_paused || ''},
					{value: 'ended', label: l.filter_ended || ''}
				];

				const fmt = (text, args = []) => {
					let out = text || '';
					args.forEach((value, i) => {
						out = out.split('%' + (i + 1) + '$s').join(String(value));
					});
					return out;
				};

				const escape_html = (text) => {
					return String(text ?? '')
						.replace(/&/g, '&amp;')
						.replace(/</g, '&lt;')
						.replace(/>/g, '&gt;')
						.replace(/"/g, '&quot;');
				};

				const fmt_number = (value) => {
					if (value === null || value === undefined || value === '') {
						return '';
					}
					if (!number_format) {
						number_format = new Intl.NumberFormat(document.documentElement.lang || undefined);
					}
					return number_format.format(value);
				};

				const fmt_compact = (value) => {
					if (!compact_format) {
						compact_format = new Intl.NumberFormat(document.documentElement.lang || undefined, {notation: 'compact', maximumFractionDigits: 1});
					}
					return compact_format.format(value);
				};

				// server delivers share as percentage 0..100
				const fmt_share = (share) => {
					if (typeof share !== 'number' || isNaN(share)) {
						return '';
					}
					return Math.round(share) + '%';
				};

				const share_style = (share) => ({
					width: Math.min(100, Math.max(0, share || 0)) + '%'
				});

				const metrics_ok = (row) => {
					return !!row.metrics_available && row.status !== 'review';
				};

				const metric = (row, value, compact = false) => {
					if (!metrics_ok(row)) {
						return l.na || '';
					}
					if (typeof value === 'number') {
						return compact ? fmt_compact(value) : fmt_number(value);
					}
					return value || l.na || '';
				};

				const rejected_html = (row) => {
					// the label embeds the fix-issues anchor with a %1$s URL placeholder;
					// without an edit URL (user may not enter the CAF) show the text only
					const html = String(l.rejected_info || '');
					const url = escape_html(row.edit_url || '');
					if (!url) {
						return html.replace(/<a\b[^>]*>(.*?)<\/a>/gi, '$1');
					}
					return html.split('%1$s').join(url);
				};

				const chip_intent = (f) => (s.filter === f.value ? 'primary' : 'secondary');

				const badge_intents = {
					review: 'primary',
					active: 'verify',
					paused: 'warning',
					ended: 'secondary',
					rejected: 'emphasis'
				};

				const badge_intent = (status) => badge_intents[status] || 'secondary';

				const row_class = (row) => ({
					'campaigns__row--rejected': row.status === 'rejected',
					'campaigns__row--paused': row.status === 'paused',
					'campaigns__row--ended': row.status === 'ended'
				});

				const na_class = (row) => ({
					'campaigns__cell--na': !metrics_ok(row)
				});

				const performance_id = (row) => ythis.id + '_performance_' + row.co_adID;

				const performance_class = (row) => ({
					'campaigns__row_performance--expanded': !!performance_expanded[row.co_adID]
				});

				const toggle_performance = (row) => {
					performance_expanded[row.co_adID] = !performance_expanded[row.co_adID];
				};

				const sync_performance_accessibility = () => {
					// The shared button does not forward disclosure ARIA into its shadow root.
					root_el.querySelectorAll('.campaigns__performance_toggle').forEach(host => {
						const button = host.shadowRoot?.querySelector('button');
						const metrics = host.closest('.campaigns__row_performance')?.querySelector('.campaigns__row_metrics');
						if (button && metrics) {
							button.setAttribute('aria-expanded', host.getAttribute('aria-expanded'));
							button.setAttribute('aria-controls', metrics.id);
							if ('ariaControlsElements' in button) {
								button.ariaControlsElements = [metrics];
							}
						}
					});
				};

				const bar_style = (row) => ({
					width: Math.min(100, Math.max(0, row.spent_percent || 0)) + '%'
				});

				const budget_style = () => {
					const pct = (s.overview && s.overview.review) ? 0 : (s.overview ? s.overview.spent_percent || 0 : 0);
					return {
						width: Math.min(100, Math.max(0, pct)) + '%'
					};
				};

				const ov_remaining_percent = () => {
					if (!s.overview) {
						return 100;
					}
					return s.overview.review ? 100 : (s.overview.remaining_percent || 0);
				};

				const ov_value = (key) => {
					if (!s.overview) {
						return '';
					}
					const value = s.overview[key];
					if (key === 'impressions' || key === 'clicks') {
						return fmt_compact(value);
					}
					if (s.overview.review) {
						if (key === 'remaining_formatted') {
							return value || s.overview.total_formatted || '';
						}
						if (key === 'ctr_formatted') {
							return value || '0.00%';
						}
						if (typeof value === 'number') {
							return fmt_number(value);
						}
						return value || fmt_number(0);
					}
					if (typeof value === 'number') {
						return fmt_number(value);
					}
					return value || l.na || '';
				};

				const table_class = () => ({
					'campaigns__table--busy': busy.value
				});

				const th_class = (value) => ({
					'campaigns__th_btn--active': s.sort === value
				});

				const sort_label = () => sort_labels[s.sort] || l.latest;
				const sort_accessible_label = () => sort_label() + ', ' + (s.descending ? l.newest : l.oldest);

				const sync_sort_selection = () => {
					const dropdown = root_el.querySelector('.campaigns__sort_menu');
					if (dropdown && customElements.get('yalwa-dropdown')) {
						dropdown.value = [s.sort, s.descending ? 'newest' : 'oldest'];
					}
				};

				const period_label = () => {
					const ov = s.overview;
					if (!ov || !ov.period_label) {
						return '';
					}
					if (ov.period_label.indexOf('%') < 0) {
						return ov.period_label;
					}
					const daily = ov.daily || [];
					const start = daily.length ? daily[0].label : (ov.daily_range || '');
					const end = daily.length > 1 ? daily[daily.length - 1].label : (ov.daily_range || '');
					// the label ships a malformed %1%s placeholder — replace manually, the label itself must not change
					return ov.period_label
						.split('%1%s').join(start)
						.split('%1$s').join(start)
						.split('%2$s').join(end);
				};

				const apply_snapshot = (snapshot) => {
					if (!snapshot) {
						return;
					}
					Object.keys(snapshot).forEach(key => {
						s[key] = snapshot[key];
					});
					if (!snapshot.overview) {
						s.overview = null;
					}
				};

				const api = async (action, data) => {
					if (in_flight || !desktop_available.value) {
						return null;
					}
					in_flight = true;
					try {
						const response = await yalwa.basics.ajaxservice({
							type: 'POST',
							resource: 'campaigns',
							action,
							csrf: csrf[action] || '',
							data
						});
						if (!response || !response.success) {
							if (response?.error === 'campaign_unavailable') {
								window.location.assign(s.dashboard_url);
								return null;
							}
							error_message.value = (response && response.message) || l.error || '';
							stop_refresh();
							return null;
						}
						error_message.value = '';
						return response.data;
					} catch (error) {
						if (error?.responseJSON?.error === 'campaign_unavailable') {
							window.location.assign(s.dashboard_url);
							return null;
						}
						error_message.value = l.error || '';
						stop_refresh();
						return null;
					} finally {
						in_flight = false;
					}
				};

				const load_state = async (overrides = {}) => {
					if (in_flight) {
						return;
					}
					busy.value = true;
					const snapshot = await api('state', {
						co_adID: 0,
						filter: s.filter,
						sort: s.sort,
						descending: s.descending,
						page: s.page,
						...overrides
					});
					busy.value = false;
					apply_snapshot(snapshot);
				};

				const set_filter = (value) => {
					if (value === s.filter || busy.value) {
						return;
					}
					load_state({filter: value, page: 1});
				};

				const select_sort = async (value) => {
					if (busy.value || !desktop_available.value) {
						sync_sort_selection();
						return;
					}
					if (value === 'newest' || value === 'oldest') {
						const descending = value === 'newest';
						if (descending !== s.descending) {
							await load_state({descending});
						}
					} else if (Object.prototype.hasOwnProperty.call(sort_labels, value) && value !== s.sort) {
						await load_state({sort: value, page: 1});
					}
					sync_sort_selection();
				};

				const set_sort_heading = (value) => {
					if (busy.value) {
						return;
					}
					if (value === s.sort) {
						load_state({descending: !s.descending});
						return;
					}
					load_state({sort: value, descending: true, page: 1});
				};

				const pager_pages = () => {
					const pages = [];
					const start = Math.max(1, Math.min(s.page - Math.floor(PAGER_WINDOW / 2), s.pages - PAGER_WINDOW + 1));
					const end = Math.min(s.pages, start + PAGER_WINDOW - 1);
					for (let p = start; p <= end; p++) {
						pages.push(p);
					}
					return pages;
				};

				const go_page = (page) => {
					if (page < 1 || page > s.pages || page === s.page || busy.value) {
						return;
					}
					load_state({page});
				};

				const go_relative_page = (delta) => {
					go_page(s.page + delta);
				};

				const campaign_action = async (action, co_adID) => {
					const id = co_adID || (s.overview ? s.overview.co_adID : 0);
					if (!id || in_flight) {
						return;
					}
					action_busy.value = true;
					const was_overview = !!(s.overview && s.overview.co_adID === id);
					const snapshot = await api(action, {co_adID: id, filter: s.filter, sort: s.sort, descending: s.descending, page: s.page});
					action_busy.value = false;
					if (!snapshot) {
						return;
					}
					apply_snapshot(snapshot);
					// row actions from the dashboard must keep the dashboard context
					if (!was_overview) {
						s.overview = null;
					}
				};

				const open_end_dialog = () => {
					if (!desktop_available.value || !end_dialog || !s.overview || end_dialog.querySelector('dialog').open) {
						return;
					}
					end_dialog.querySelector('.js-campaigns_end_confirmation').textContent = s.overview.end_confirmation;
					end_dialog.querySelector('.js-campaigns_end_info').innerHTML = s.overview.end_info;
					end_dialog.querySelector('.js-campaigns_confirm_end').disabled = action_busy.value;
					close_dropdowns();
					end_dialog.show();
				};

				const close_end_dialog = () => {
					end_dialog?.close();
				};

				const confirm_end = () => {
					const id = s.overview ? s.overview.co_adID : 0;
					close_end_dialog();
					campaign_action('end', id);
				};

				const close_dropdowns = () => {
					root_el.querySelectorAll('yalwa-dropdown').forEach(dropdown => dropdown.close?.());
				};

				const select_export = (value) => {
					if (value === 'export' && desktop_available.value && s.overview?.can_export && s.overview.export_url) {
						window.location.assign(s.overview.export_url);
					}
				};

				const subnav_dashboard = () => {
					if (s.overview) {
						back();
					}
				};

				const back = () => {
					if (s.dashboard_url) {
						window.location.assign(s.dashboard_url);
					}
				};

				const toggle_city = (key) => {
					expanded[key] = !expanded[key];
				};

				const overview_class = () => ({
					'campaigns__state--paused': s.overview?.status === 'paused',
					'campaigns__state--ended': s.overview?.status === 'ended',
					'campaigns__state--review': !!s.overview?.review
				});

				const chart_metric_label = () => chart_metrics.find(metric => metric.value === chart_metric.value)?.label || '';

				const chart_range = () => {
					const daily = s.overview?.daily || [];
					return daily.length ? daily[0].label + ' – ' + daily[daily.length - 1].label : '';
				};

				const set_chart_metric = (value) => {
					if (chart_metrics.some(metric => metric.value === value)) {
						chart_metric.value = value;
					}
				};

				const build_chart_options = (el, selected_metric, data_max) => {
					const style = getComputedStyle(document.documentElement);
					const token = name => style.getPropertyValue(name).trim();
					const font = getComputedStyle(el);
					const text_style = {color: token('--color_text_tertiary'), fontSize: parseFloat(font.fontSize)};
					// Google Charts does not accept RGBA grid colors; flatten the LDS border over the card surface.
					const canvas = document.createElement('canvas');
					canvas.width = canvas.height = 1;
					const context = canvas.getContext('2d');
					context.fillStyle = token('--color_surface_inverse');
					context.fillRect(0, 0, 1, 1);
					context.fillStyle = token('--color_border_light');
					context.fillRect(0, 0, 1, 1);
					const grid_color = '#' + Array.from(context.getImageData(0, 0, 1, 1).data).slice(0, 3).map(channel => channel.toString(16).padStart(2, '0')).join('');
					return {
						legend: 'none',
						backgroundColor: 'transparent',
						fontName: font.fontFamily.split(',')[0].replace(/['"]/g, ''),
						bar: {groupWidth: '80%'},
						width: el.clientWidth,
						height: 180,
						chartArea: {left: 40, top: 8, width: Math.max(0, el.clientWidth - 40), height: 148},
						colors: [token('--color_background_brand_bold')],
						hAxis: {textStyle: text_style, baselineColor: grid_color},
						vAxis: {
							// An all-zero range is degenerate: Google Charts expands it into a
							// symmetric window, which would draw negative ticks on an empty chart.
							viewWindow: data_max > 0 ? {min: 0} : {min: 0, max: 1},
							format: selected_metric === 'spent' ? '0.00' : 'short',
							textStyle: text_style,
							gridlines: {color: grid_color, count: 5},
							minorGridlines: {count: 0},
							baselineColor: grid_color
						}
					};
				};

				const render_chart = (google, current_chart, data, options) => new Promise((resolve, reject) => {
					const events = google.visualization.events;
					let ready_listener = null;
					let error_listener = null;
					const finish = (error) => {
						clearTimeout(timer);
						events.removeListener(ready_listener);
						events.removeListener(error_listener);
						if (error) {
							reject(error);
						} else {
							resolve();
						}
					};
					const timer = setTimeout(() => finish(new Error('Chart draw timeout')), CHART_LOAD_TIMEOUT_MS);
					ready_listener = events.addListener(current_chart, 'ready', () => finish());
					error_listener = events.addListener(current_chart, 'error', () => finish(new Error('Chart draw failed')));
					try {
						current_chart.draw(data, options);
					} catch (error) {
						finish(error);
					}
				});

				const stripe_chart = (el, color) => {
					const svg = el.querySelector('svg');
					if (!svg) {
						return;
					}
					const namespace = 'http://www.w3.org/2000/svg';
					const patternID = ythis.id + '_chart_stripes';
					const defs = document.createElementNS(namespace, 'defs');
					const pattern = document.createElementNS(namespace, 'pattern');
					pattern.setAttribute('id', patternID);
					pattern.setAttribute('width', '8');
					pattern.setAttribute('height', '8');
					pattern.setAttribute('patternUnits', 'userSpaceOnUse');
					pattern.setAttribute('patternTransform', 'rotate(75)');
					const stripe = document.createElementNS(namespace, 'rect');
					stripe.setAttribute('width', '4');
					stripe.setAttribute('height', '8');
					stripe.setAttribute('fill', getComputedStyle(document.documentElement).getPropertyValue('--color_surface_inverse').trim());
					stripe.setAttribute('fill-opacity', '0.25');
					pattern.appendChild(stripe);
					defs.appendChild(pattern);
					svg.prepend(defs);
					svg.querySelectorAll('rect').forEach(bar => {
						if (bar.getAttribute('fill')?.toLowerCase() === color.toLowerCase()) {
							const overlay = bar.cloneNode();
							overlay.setAttribute('fill', 'url(#' + patternID + ')');
							overlay.setAttribute('pointer-events', 'none');
							bar.after(overlay);
						}
					});
				};

				const draw_chart = async () => {
					const version = ++chart_version;
					const el = root_el.querySelector('.js-campaigns_chart');
					const overview = s.overview;
					const selected_metric = chart_metric.value;
					if (!el || !overview || overview.review || disposed || !desktop_available.value) {
						return;
					}
					try {
						chart_failed.value = false;
						await Vue.nextTick();
						if (!el.clientWidth) {
							return;
						}
						const google = await load_google_charts(CHART_LOAD_TIMEOUT_MS);
						await load_corecharts(google, CHART_LOAD_TIMEOUT_MS);
						if (disposed || version !== chart_version || !el.isConnected || !desktop_available.value) {
							return;
						}
						const data_table = new google.visualization.DataTable();
						const weekday_format = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {weekday: 'short'});
						data_table.addColumn('string', '');
						data_table.addColumn('number', chart_metrics.find(metric => metric.value === selected_metric).label);
						let data_max = 0;
						(overview.daily || []).forEach(day => {
							const date = new Date(day.date + 'T12:00:00');
							const value = Number(day[selected_metric]) || 0;
							data_max = Math.max(data_max, value);
							data_table.addRow([weekday_format.format(date), value]);
						});
						chart_data = data_table;
						const options = build_chart_options(el, selected_metric, data_max);
						if (chart) {
							chart.clearChart();
						} else {
							chart = new google.visualization.ColumnChart(el);
						}
						await render_chart(google, chart, data_table, options);
						if (disposed || version !== chart_version || !el.isConnected || !desktop_available.value) {
							return;
						}
						if (overview.status === 'paused') {
							stripe_chart(el, options.colors[0]);
						}
					} catch {
						if (!disposed && version === chart_version) {
							chart?.clearChart();
							chart = null;
							chart_data = null;
							chart_failed.value = true;
						}
					}
				};

				const on_resize = () => {
					if (resize_timer) {
						clearTimeout(resize_timer);
					}
					resize_timer = setTimeout(() => {
						if (chart && chart_data && !chart_failed.value) {
							draw_chart();
						}
					}, 200);
				};

				const should_refresh = () => {
					return !disposed
						&& desktop_available.value
						&& !document.hidden
						&& !in_flight
						&& !error_message.value
						&& !!s.overview
						&& (['active', 'review', 'paused'].includes(s.overview.status) || action_busy.value);
				};

				const tick = async () => {
					if (!should_refresh()) {
						return;
					}
					const co_adID = s.overview.co_adID;
					const snapshot = await api('state', {
						co_adID,
						filter: s.filter,
						sort: s.sort,
						descending: s.descending,
						page: s.page
					});
					if (snapshot && s.overview && s.overview.co_adID === co_adID) {
						apply_snapshot(snapshot);
					}
				};

				const stop_refresh = () => {
					if (refresh_timer) {
						clearInterval(refresh_timer);
						refresh_timer = null;
					}
				};

				const start_refresh = () => {
					if (!refresh_timer && !disposed && desktop_available.value) {
						refresh_timer = setInterval(tick, REFRESH_INTERVAL_MS);
					}
				};

				const on_visibility = () => {
					if (document.hidden) {
						stop_refresh();
					} else if (should_refresh()) {
						start_refresh();
					}
				};

				Vue.watch(
					() => [s.overview ? s.overview.status : null, action_busy.value, error_message.value],
					() => {
						if (s.overview && (['active', 'review', 'paused'].includes(s.overview.status) || action_busy.value) && !error_message.value) {
							start_refresh();
						} else {
							stop_refresh();
						}
					}
				);

				Vue.watch(
					() => s.overview,
					() => {
						chart_version++;
						chart?.clearChart();
						chart = null;
						chart_data = null;
						chart_failed.value = false;
						Vue.nextTick(draw_chart);
					}
				);

				Vue.watch(() => [s.sort, s.descending], sync_sort_selection, {flush: 'post'});
				Vue.watch(chart_metric, () => Vue.nextTick(draw_chart));
				Vue.watch(() => [s.campaigns, {...performance_expanded}], sync_performance_accessibility, {flush: 'post'});

				const leave_campaigns = () => {
					window.location.assign(s.account_url);
				};

				const update_desktop_gate = () => {
					desktop_available.value = supported_screen_media.matches;
					if (desktop_available.value) {
						desktop_dialog?.close();
						Vue.nextTick(draw_chart);
						start_refresh();
					} else {
						chart_version++;
						stop_refresh();
						close_dropdowns();
						close_end_dialog();
						if (desktop_dialog && !desktop_dialog.querySelector('dialog').open) {
							desktop_dialog.show();
						}
					}
				};

				const connect_dialogs = async () => {
					await customElements.whenDefined('yalwa-dialog');
					if (disposed) {
						return;
					}
					end_dialog = document.getElementById(ythis.id + '_end_dialog');
					desktop_dialog = document.getElementById(ythis.id + '_desktop_dialog');
					if (!end_dialog || !desktop_dialog) {
						console.error('Campaign dialogs are missing');
						update_desktop_gate();
						return;
					}
					[end_dialog, desktop_dialog].forEach(dialog => {
						const title = dialog.querySelector('.campaigns_dialog__title');
						title.id = dialog.id + '_title';
						dialog.querySelector('dialog').setAttribute('aria-labelledby', title.id);
					});
					const listeners = [];
					const listen = (element, event, handler) => {
						element.addEventListener(event, handler);
						listeners.push(() => element.removeEventListener(event, handler));
					};
					listen(end_dialog.querySelector('.js-campaigns_keep'), 'click', close_end_dialog);
					listen(end_dialog.querySelector('.js-campaigns_confirm_end'), 'click', confirm_end);
					listen(end_dialog.querySelector('dialog'), 'keydown', (event) => {
						if (event.key === 'Escape') {
							event.preventDefault();
							close_end_dialog();
						}
					});
					listen(desktop_dialog.querySelector('.js-campaigns_desktop_acknowledge'), 'click', leave_campaigns);
					listen(desktop_dialog.querySelector('dialog'), 'keydown', (event) => {
						if (event.key === 'Escape') {
							event.preventDefault();
							leave_campaigns();
						}
					});
					listen(desktop_dialog.querySelector('dialog'), 'cancel', (event) => {
						event.preventDefault();
						leave_campaigns();
					});
					remove_dialog_listeners = () => listeners.forEach(remove => remove());
					update_desktop_gate();
				};

				Vue.onMounted(() => {
					window.addEventListener('resize', on_resize);
					supported_screen_media.addEventListener('change', update_desktop_gate);
					document.addEventListener('visibilitychange', on_visibility);
					connect_dialogs();
					customElements.whenDefined('yalwa-dropdown').then(() => {
						if (!disposed) {
							Vue.nextTick(sync_sort_selection);
						}
					});
					customElements.whenDefined('yalwa-button').then(() => {
						if (!disposed) {
							Vue.nextTick(sync_performance_accessibility);
						}
					});
				});

				Vue.onUnmounted(() => {
					disposed = true;
					chart_version++;
					clearTimeout(resize_timer);
					stop_refresh();
					remove_dialog_listeners();
					end_dialog?.close();
					desktop_dialog?.close();
					supported_screen_media.removeEventListener('change', update_desktop_gate);
					window.removeEventListener('resize', on_resize);
					document.removeEventListener('visibilitychange', on_visibility);
					close_dropdowns();
					if (chart) {
						chart.clearChart();
						chart = null;
					}
				});

				return {
					s,
					l,
					filters,
					expanded,
					performance_expanded,
					performance_id,
					performance_class,
					toggle_performance,
					busy,
					action_busy,
					error_message,
					chart_failed,
					chart_metric,
					chart_metrics,
					chart_metric_label,
					chart_range,
					set_chart_metric,
					overview_class,
					desktop_available,
					sort_label,
					sort_accessible_label,
					fmt,
					fmt_number,
					fmt_compact,
					fmt_share,
					share_style,
					metrics_ok,
					metric,
					rejected_html,
					ov_value,
					ov_remaining_percent,
					table_class,
					th_class,
					chip_intent,
					badge_intent,
					row_class,
					na_class,
					bar_style,
					budget_style,
					period_label,
					set_filter,
					select_sort,
					set_sort_heading,
					pager_pages,
					go_page,
					go_relative_page,
					campaign_action,
					open_end_dialog,
					close_end_dialog,
					confirm_end,
					select_export,
					subnav_dashboard,
					toggle_city
				};
			}
		});
	}
}
